import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowRight, ArrowUpRight, Compass, PlayCircle } from "lucide-react";
import { useSite, useBrand } from "@/context/SiteContext";
import { useSeo } from "@/lib/seo";
import { Reveal, Stagger, staggerItem } from "@/components/Reveal";
import EnergyDiagram from "@/components/EnergyDiagram";
import Marquee from "@/components/Marquee";
import CTASection from "@/components/CTASection";
import { HERO_HIGHLIGHTS, WHY_US, PRODUCTS, SOLUTIONS, BRANDS, PROJECT_CATEGORIES, RESOURCE_TOPICS, brandify } from "@/data/content";

const heroLines = ["Complete Solar,", "Energy Storage &", "Power Solutions"];

function Chapter({ n, label }) {
  return <div className="flex items-center gap-3 mb-4"><span className="font-mono text-emerald-400 text-sm">{n}</span><span className="mono-label">{label}</span><div className="flex-1 h-px bg-white/10" /></div>;
}

export default function Home() {
  const { openQuote, waLink } = useSite();
  const brand = useBrand();
  useSeo({
    title: `${brand.name} | Solar Inverters, Hybrid Inverters & Energy Solutions`,
    description: `${brand.name} provides solar inverters, hybrid inverters, UPS, LFP batteries, ESS, EV charging, solar BOS and complete solar EPC solutions for residential, commercial, industrial and institutional applications.`,
    canonical: `https://${brand.domain}/`,
  });

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(16,185,129,0.15),transparent_45%),radial-gradient(circle_at_85%_15%,rgba(56,189,248,0.08),transparent_40%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 lg:pt-24 lg:pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="badge-terminal mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Solar + Storage + Power Management
            </motion.div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-white">
              {heroLines.map((line, i) => (
                <span key={i} className="block overflow-hidden">
                  <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.8, delay: 0.15 + i * 0.12, ease: [0.2, 0.8, 0.2, 1] }}>
                    {i === 2 ? <span className="text-gradient">{line}</span> : line}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }} className="text-slate-300 text-base sm:text-lg mt-6 max-w-xl leading-relaxed">
              Reliable and scalable solutions for solar power generation, energy storage and power backup — from advanced hybrid inverters and LFP batteries to UPS systems, EV charging and complete solar EPC projects.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85 }} className="flex flex-wrap gap-3 mt-8">
              <button onClick={() => openQuote("Hero")} className="btn-primary" data-testid="hero-find-solution-button"><Compass size={18} /> Find Your Energy Solution</button>
              <Link to="/products" className="btn-ghost" data-testid="hero-explore-products-button">Explore Products <ArrowRight size={16} /></Link>
              <button onClick={() => openQuote("Hero Expert")} className="btn-ghost" data-testid="hero-talk-expert-button">Talk to an Expert</button>
            </motion.div>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.3} className="card-hero p-5 sm:p-7 rounded-2xl bg-[#0B1522]/70 border border-emerald-400/20 backdrop-blur-md">
              <div className="mono-label mb-2">Live Energy Ecosystem</div>
              <EnergyDiagram />
            </Reveal>
          </div>
        </div>
      </section>

      <Marquee />

      {/* HIGHLIGHTS: Solar | Storage | Backup | EV */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <Stagger className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {HERO_HIGHLIGHTS.map((h) => {
            const Icon = Icons[h.icon] || Icons.Sun;
            return (
              <motion.div key={h.title} variants={staggerItem}>
                <Link to={h.to} data-testid={`highlight-${h.title.toLowerCase().replace(/\s+/g, "-")}`} className="card-surface p-6 block h-full group">
                  <Icon className="text-emerald-400 mb-4" size={28} />
                  <div className="font-display font-semibold text-white text-lg">{h.title}</div>
                  <div className="text-xs text-slate-400 mt-1 font-mono">{h.sub}</div>
                  <ArrowUpRight className="text-emerald-400 mt-4 opacity-0 group-hover:opacity-100 transition-opacity" size={18} />
                </Link>
              </motion.div>
            );
          })}
        </Stagger>
      </section>

      {/* ABOUT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <Chapter n="01" label={`About ${brand.name}`} />
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white leading-tight">Your Trusted Partner for Solar & Energy Solutions</h2>
          <p className="text-slate-300 mt-5 leading-relaxed">{brandify("Hindustan Solar Power is a renewable energy and power solutions company serving customers, dealers, EPC companies, institutions and government organizations with a comprehensive range of solar, energy storage and power backup solutions.", brand.name)}</p>
          <p className="text-slate-400 mt-4 leading-relaxed">Our core expertise lies in On-Grid, Off-Grid and Hybrid Solar Inverters, with a strong focus on next-generation hybrid energy systems that combine solar generation, battery storage and grid power for greater energy independence and reliability.</p>
          <Link to="/about" className="btn-ghost mt-6 inline-flex" data-testid="about-learn-more-button">Learn More About Us <ArrowRight size={16} /></Link>
        </Reveal>
        <Reveal delay={0.15}>
          <img src={PRODUCTS[1].image} alt="Hybrid solar inverter and energy storage system" loading="lazy"
            className="rounded-2xl w-full h-[420px] object-cover border border-white/10" />
        </Reveal>
      </section>

      {/* CORE PRODUCTS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <Reveal><Chapter n="02" label="Core Products" />
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-10">A Complete Energy Portfolio Under One Roof</h2></Reveal>
        <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PRODUCTS.slice(0, 6).map((p) => (
            <motion.div key={p.slug} variants={staggerItem}>
              <Link to={`/products/${p.slug}`} data-testid={`product-card-${p.slug}`} className="card-surface block overflow-hidden group h-full">
                <div className="h-44 overflow-hidden"><img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                <div className="p-6">
                  <div className="mono-label">{p.tag}</div>
                  <div className="font-display font-semibold text-white text-lg mt-1">{p.name}</div>
                  <p className="text-sm text-slate-400 mt-2 line-clamp-2">{p.sub}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </Stagger>
        <div className="text-center mt-10"><Link to="/products" className="btn-ghost inline-flex">View All Products <ArrowRight size={16} /></Link></div>
      </section>

      {/* WHY US */}
      <section className="bg-[#04261C]/30 border-y border-white/5 py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal><Chapter n="03" label="Why Choose Us" />
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-10">Why Choose {brand.name}?</h2></Reveal>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_US.map((w) => (
              <motion.div key={w.n} variants={staggerItem} className="card-surface p-7">
                <div className="font-mono text-4xl font-bold text-emerald-500/30">{w.n}</div>
                <div className="font-display font-semibold text-white text-lg mt-2">{w.title}</div>
                <p className="text-sm text-slate-400 mt-2 leading-relaxed">{w.text}</p>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* HYBRID EXPLANATION + SOLAR+BATTERY */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal className="order-2 lg:order-1">
          <div className="card-surface p-6 sm:p-8"><EnergyDiagram /></div>
        </Reveal>
        <Reveal delay={0.15} className="order-1 lg:order-2">
          <Chapter n="04" label="Hybrid Energy & Solar + Battery" />
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white leading-tight">One System. Solar + Battery + Grid.</h2>
          <p className="text-slate-300 mt-5 leading-relaxed">Hybrid solar inverters represent the next generation of intelligent energy management. They combine solar generation, battery storage and grid power into a single integrated system that prioritises solar, charges batteries when required and provides backup during grid outages.</p>
          <div className="flex flex-wrap gap-2 mt-6">
            {["Reduced grid dependency", "Backup during power cuts", "Intelligent management", "Scalable design"].map((b) => (
              <span key={b} className="badge-terminal">{b}</span>
            ))}
          </div>
          <Link to="/products/hybrid-solar-inverters" className="btn-primary mt-7 inline-flex" data-testid="hybrid-cta-button">Find the Right Hybrid Inverter <ArrowRight size={16} /></Link>
        </Reveal>
      </section>

      {/* EPC */}
      <section className="bg-[#04261C]/30 border-y border-white/5 py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal><Chapter n="05" label="Complete Solar EPC" />
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-10">Engineering, Procurement, Construction & Commissioning</h2></Reveal>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SOLUTIONS[0].stages.map((s, i) => (
              <motion.div key={s.title} variants={staggerItem} className="card-surface p-6">
                <div className="font-mono text-emerald-400 text-sm">{String(i + 1).padStart(2, "0")}</div>
                <div className="font-display font-semibold text-white text-lg mt-1">{s.title}</div>
                <ul className="mt-3 space-y-1">
                  {s.items.slice(0, 4).map((it) => <li key={it} className="text-xs text-slate-400">— {it}</li>)}
                </ul>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* B2B + GOVT */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid lg:grid-cols-2 gap-6">
        {[SOLUTIONS[6], SOLUTIONS[5]].map((s, i) => (
          <Reveal key={s.slug} delay={i * 0.1}>
            <Link to={`/solutions/${s.slug}`} className="card-surface block h-full overflow-hidden group" data-testid={`home-solution-${s.slug}`}>
              <div className="h-52 overflow-hidden"><img src={s.image} alt={s.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
              <div className="p-7">
                <div className="mono-label">{s.sector}</div>
                <h3 className="font-display text-2xl font-bold text-white mt-1">{s.hero}</h3>
                <p className="text-slate-400 mt-3 line-clamp-3">{brandify(s.intro, brand.name)}</p>
                <span className="text-emerald-400 mt-4 inline-flex items-center gap-1 text-sm font-medium">Explore <ArrowRight size={15} /></span>
              </div>
            </Link>
          </Reveal>
        ))}
      </section>

      {/* BRANDS */}
      <section className="bg-[#04261C]/30 border-y border-white/5 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal><Chapter n="06" label="Our Brands" />
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-8">Multi-Brand Product Availability</h2></Reveal>
          <Stagger className="flex flex-wrap gap-3">
            {Object.values(BRANDS).flat().map((b) => (
              <motion.span key={b} variants={staggerItem} className="px-5 py-3 rounded-xl bg-[#0B1522] border border-white/10 text-slate-300 font-display font-medium hover:border-emerald-400/40 transition-colors">{b}</motion.span>
            ))}
          </Stagger>
          <Link to="/brands" className="btn-ghost mt-8 inline-flex">View All Brands <ArrowRight size={16} /></Link>
        </div>
      </section>

      {/* PROJECTS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <Reveal><Chapter n="07" label="Projects" />
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-10">Where We Deliver</h2></Reveal>
        <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PROJECT_CATEGORIES.map((p) => (
            <motion.div key={p.title} variants={staggerItem} className="card-surface p-7">
              <div className="font-display font-semibold text-white text-lg">{p.title}</div>
              <p className="text-sm text-slate-400 mt-2">{p.text}</p>
            </motion.div>
          ))}
        </Stagger>
      </section>

      {/* RESOURCES */}
      <section className="bg-[#04261C]/30 border-y border-white/5 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal><Chapter n="08" label="Resources & Knowledge" />
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white mb-8">Learn Before You Invest</h2></Reveal>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {RESOURCE_TOPICS.slice(0, 6).map((r) => (
              <motion.div key={r.title} variants={staggerItem}>
                <Link to="/resources" className="card-surface p-6 block h-full">
                  <div className="font-display font-semibold text-white">{r.title}</div>
                  <p className="text-sm text-slate-400 mt-2">{r.text}</p>
                </Link>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
